import React, { useEffect, useState } from 'react';
import { Search, Shield } from 'lucide-react';
import { adminAPI } from '../../services/api';
import UserDetailDrawer from '../../components/admin/UserDetailDrawer';

const formatRelativeDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const days = Math.floor((Date.now() - d.getTime()) / 86400000);
  if (days < 1) return 'today';
  if (days < 30) return `${days} day${days === 1 ? '' : 's'} ago`;
  if (days < 365) return `${Math.floor(days / 30)} mo ago`;
  return `${Math.floor(days / 365)} yr ago`;
};

const AdminUsers = () => {
  const [users, setUsers] = useState([]);
  const [pagination, setPagination] = useState({});
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [drawerUserId, setDrawerUserId] = useState(null);
  const [error, setError] = useState(null);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const res = await adminAPI.getUsers({ page, per_page: 50, q: search });
      setUsers(res.data || []);
      setPagination(res.pagination || {});
    } catch (e) {
      setError(e?.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchUsers(); /* eslint-disable-next-line */ }, [page, search]);

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-foreground">Users</h1>
        <div className="relative w-72">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground pointer-events-none" />
          <input
            type="text"
            value={search}
            onChange={(e) => { setPage(1); setSearch(e.target.value); }}
            placeholder="Search by name or email"
            className="pl-9 pr-3 py-2 w-full text-sm border border-input rounded-lg focus:ring-black focus:border-black"
          />
        </div>
      </div>

      {error && <div className="mb-3 p-3 bg-red-50 text-red-700 text-sm rounded-lg">{error}</div>}

      <div className="bg-card rounded-lg border border-border overflow-hidden mb-6">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-muted/50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Email</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Workspaces (role)</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Created</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {loading ? (
              <tr><td colSpan={3} className="px-6 py-8 text-center text-sm text-muted-foreground">Loading...</td></tr>
            ) : users.length === 0 ? (
              <tr><td colSpan={3} className="px-6 py-8 text-center text-sm text-muted-foreground">No users.</td></tr>
            ) : users.map((u) => (
              <tr key={u.id}
                  className="cursor-pointer hover:bg-muted/50"
                  onClick={() => setDrawerUserId(u.id)}>
                <td className="px-4 py-3 text-sm text-foreground">
                  <div className="flex items-center gap-2">
                    {u.email}
                    {u.internal_admin && <Shield className="h-4 w-4 text-blue-600" />}
                  </div>
                  <div className="text-xs text-muted-foreground">{u.name}</div>
                </td>
                <td className="px-4 py-3 text-sm text-foreground">
                  {(u.memberships || []).map((m) => `${m.client_name} (${m.role})`).join(' · ') || '—'}
                </td>
                <td className="px-4 py-3 text-sm text-muted-foreground">{formatRelativeDate(u.created_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-4 flex items-center justify-between text-sm text-muted-foreground">
        <div>
          Showing page {pagination.current_page || 1} of {pagination.total_pages || 1} · {pagination.total_count || 0} total
        </div>
        <div className="flex gap-2">
          <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1}
                  className="px-3 py-1 border border-input rounded disabled:opacity-50">Previous</button>
          <button onClick={() => setPage((p) => p + 1)} disabled={page >= (pagination.total_pages || 1)}
                  className="px-3 py-1 border border-input rounded disabled:opacity-50">Next</button>
        </div>
      </div>

      <UserDetailDrawer
        open={!!drawerUserId}
        userId={drawerUserId}
        onClose={() => setDrawerUserId(null)}
        onChanged={fetchUsers}
      />
    </div>
  );
};

export default AdminUsers;
